import React, { useState, useEffect, useCallback } from 'react';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';

const AuditLogs = () => {
  const { getAuditLogs, getUsers } = useSuperadminAuth();
  
  // Data state
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0
  });

  // Filter state
  const [filters, setFilters] = useState({
    q: '',
    action: 'all',
    actor: '',
    from: '',
    to: '',
    sort: 'created_at',
    order: 'desc'
  });

  // UI state
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);

  // Load audit logs
  const loadLogs = useCallback(async (page = 1, newFilters = filters) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        pageSize: pagination.pageSize,
        ...newFilters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });

      const response = await getAuditLogs(params);
      
      if (response.ok) {
        setLogs(response.data);
        setPagination(response.pagination);
        setSummary(response.summary);
      } else {
        setError('Failed to load audit logs');
      }
    } catch (err) {
      console.error('Error loading audit logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuditLogs, filters, pagination.pageSize]);

  // Load users for filters
  const loadFilterOptions = useCallback(async () => {
    try {
      const usersResponse = await getUsers({ pageSize: 100, role: 'superadmin' });
      
      if (usersResponse.ok) {
        setUsers(usersResponse.data);
      }
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  }, [getUsers]);

  // Load initial data
  useEffect(() => {
    loadLogs();
    loadFilterOptions();
  }, [loadLogs, loadFilterOptions]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    loadLogs(1, newFilters);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    loadLogs(newPage);
  };

  // Handle sort change
  const handleSort = (column) => {
    const newOrder = filters.sort === column && filters.order === 'asc' ? 'desc' : 'asc';
    const newFilters = { ...filters, sort: column, order: newOrder };
    setFilters(newFilters);
    loadLogs(1, newFilters);
  };

  // Handle CSV export
  const handleExport = async () => {
    try {
      setExporting(true);
      
      const params = {
        ...filters,
        export_csv: true
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });

      // Create a temporary link to trigger download
      const queryString = new URLSearchParams(params).toString();
      const url = `/api/superadmin/audit-log?${queryString}`;
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error exporting logs:', err);
      setError('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  // Helper components
  const ActionBadge = ({ action }) => {
    const colors = {
      impersonate_start: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      impersonate_end: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      shop_status_change: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      user_disable: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      user_enable: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[action] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>
        {action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    );
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const SortIcon = ({ column }) => {
    if (filters.sort !== column) return <span className="text-gray-400">↕️</span>;
    return filters.order === 'asc' ? '↗️' : '↘️';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Audit Logs
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Complete audit trail of administrative actions
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          {summary && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {summary.total_filtered} events found
            </div>
          )}
          
          <button
            onClick={handleExport}
            disabled={exporting || logs.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          
          <button
            onClick={() => loadLogs(pagination.page)}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && summary.action_counts && Object.keys(summary.action_counts).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Object.entries(summary.action_counts).map(([action, count]) => (
            <div key={action} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{count}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  <ActionBadge action={action} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search
            </label>
            <input
              type="text"
              placeholder="User name or email..."
              value={filters.q}
              onChange={(e) => handleFilterChange('q', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Action Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Action Type
            </label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Actions</option>
              <option value="impersonate_start">Impersonate Start</option>
              <option value="impersonate_end">Impersonate End</option>
              <option value="shop_status_change">Shop Status Change</option>
              <option value="user_disable">User Disable</option>
              <option value="user_enable">User Enable</option>
            </select>
          </div>

          {/* Actor Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Actor
            </label>
            <select
              value={filters.actor}
              onChange={(e) => handleFilterChange('actor', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Users</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => handleFilterChange('from', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => handleFilterChange('to', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Sort */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Sort By
            </label>
            <select
              value={`${filters.sort}_${filters.order}`}
              onChange={(e) => {
                const [sort, order] = e.target.value.split('_');
                setFilters({ ...filters, sort, order });
                loadLogs(1, { ...filters, sort, order });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="timestamp_desc">Newest First</option>
              <option value="timestamp_asc">Oldest First</option>
              <option value="action_type_asc">Action A-Z</option>
              <option value="action_type_desc">Action Z-A</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => loadLogs(pagination.page)}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* Audit Logs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('timestamp')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Timestamp</span>
                    <SortIcon column="timestamp" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('action_type')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Action</span>
                    <SortIcon column="action_type" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Shop
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  IP Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    No audit logs found matching your criteria
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {formatTimestamp(log.timestamp)}
                    </td>
                    <td className="px-6 py-4">
                      <ActionBadge action={log.action_type} />
                    </td>
                    <td className="px-6 py-4">
                      {log.actor ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {log.actor.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {log.actor.email}
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            {log.actor.role}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">System</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {log.target_user ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {log.target_user.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {log.target_user.email}
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            {log.target_user.role}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {log.shop ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {log.shop.name}
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            /{log.shop.slug}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {log.ip_address || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => {
                          setSelectedLog(log);
                          setShowLogModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
              {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
              {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const page = Math.max(1, Math.min(pagination.totalPages - 4, pagination.page - 2)) + i;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 border rounded text-sm ${
                      page === pagination.page
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Log Details Modal */}
      {showLogModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Audit Log Details
              </h3>
              <button
                onClick={() => setShowLogModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Log ID
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">{selectedLog.id}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Timestamp
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {formatTimestamp(selectedLog.timestamp)}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Action Type
                  </label>
                  <div className="mt-1">
                    <ActionBadge action={selectedLog.action_type} />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    IP Address
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedLog.ip_address || 'Unknown'}
                  </p>
                </div>
              </div>

              {/* Actor Details */}
              {selectedLog.actor && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Actor Details
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                          Name
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedLog.actor.name}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                          Email
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedLog.actor.email}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                          Role
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedLog.actor.role}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Target User Details */}
              {selectedLog.target_user && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Target User Details
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                          Name
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedLog.target_user.name}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                          Email
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedLog.target_user.email}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                          Role
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedLog.target_user.role}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Shop Details */}
              {selectedLog.shop && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Shop Details
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                          Shop Name
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedLog.shop.name}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                          Shop Slug
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          /{selectedLog.shop.slug}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Technical Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Technical Details
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                        User Agent
                      </label>
                      <p className="text-xs text-gray-900 dark:text-white font-mono break-all">
                        {selectedLog.user_agent || 'Unknown'}
                      </p>
                    </div>
                    
                    {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                          Metadata
                        </label>
                        <pre className="text-xs text-gray-900 dark:text-white bg-white dark:bg-gray-800 p-2 rounded border overflow-x-auto">
                          {JSON.stringify(selectedLog.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowLogModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;